/* ═══════════════════════════════════════════════════════════════
   Timesheets rebuild — one hour-entry cell.

   - Local draft state; commits on blur/Enter only (never per
     keystroke, never a full-week reload from here — the parent's
     onCommit is a single-cell optimistic upsert).
   - Forecast ghost renders as a muted placeholder; Enter on an empty
     ghosted cell accepts it explicitly (D7 — nothing writes on its
     own).
   - Copy/Cut/Paste triad: right-click menu AND Ctrl/Cmd+C/X/V,
     backed by the shared hour-value clipboard (spreadsheet-style,
     not the browser's text clipboard).
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useRef, useState } from 'react';
import { rem } from '../../lib/rem';
import { CellContextMenu } from './CellContextMenu';
import { copyHourValue, hasHourClipboard, readHourClipboard } from './hourClipboard';

export interface HourCellProps {
  value: number;
  ghost?: number;
  disabled?: boolean;
  isToday?: boolean;
  onCommit: (hours: number) => void;
  ariaLabel?: string;
}

export function HourCell({ value, ghost, disabled, isToday, onCommit, ariaLabel }: HourCellProps) {
  const [draft, setDraft] = useState<string>(value > 0 ? String(value) : '');
  const [focused, setFocused] = useState(false);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync from the committed value whenever it changes elsewhere
  // (optimistic update landing, reload) — but never clobber active typing.
  useEffect(() => {
    if (!focused) setDraft(value > 0 ? String(value) : '');
  }, [value, focused]);

  function clamp(n: number): number {
    return Math.max(0, Math.min(24, n));
  }

  function commit(raw: string) {
    const parsed = raw.trim() === '' ? 0 : clamp(parseFloat(raw) || 0);
    setDraft(parsed > 0 ? String(parsed) : '');
    if (parsed !== value) onCommit(parsed);
  }

  function acceptGhost() {
    if (ghost === undefined || ghost <= 0) return;
    setDraft(String(ghost));
    onCommit(ghost);
  }

  const hasGhost = value === 0 && ghost !== undefined && ghost > 0;

  return (
    <div
      style={{ position: 'relative' }}
      onContextMenu={(e) => {
        if (disabled) return;
        e.preventDefault();
        setMenuPos({ x: e.clientX, y: e.clientY });
      }}
    >
      <input
        ref={inputRef}
        type="number"
        min={0}
        max={24}
        step={0.25}
        value={draft}
        aria-label={ariaLabel}
        disabled={disabled}
        placeholder={hasGhost ? String(ghost) : '–'}
        onFocus={() => setFocused(true)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setFocused(false);
          commit(draft);
        }}
        onKeyDown={(e) => {
          const mod = e.ctrlKey || e.metaKey;
          if (mod && (e.key === 'c' || e.key === 'C')) {
            e.preventDefault();
            copyHourValue(draft.trim() === '' ? 0 : parseFloat(draft) || 0);
            return;
          }
          if (mod && (e.key === 'x' || e.key === 'X')) {
            e.preventDefault();
            copyHourValue(draft.trim() === '' ? 0 : parseFloat(draft) || 0);
            setDraft('');
            commit('');
            return;
          }
          if (mod && (e.key === 'v' || e.key === 'V')) {
            e.preventDefault();
            const clip = readHourClipboard();
            if (clip !== null) {
              setDraft(clip > 0 ? String(clip) : '');
              commit(String(clip));
            }
            return;
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            if (draft.trim() === '' && hasGhost) {
              acceptGhost();
            } else {
              commit(draft);
            }
            inputRef.current?.blur();
          } else if (e.key === 'Escape') {
            setDraft(value > 0 ? String(value) : '');
            inputRef.current?.blur();
          }
        }}
        style={{
          padding: `${rem(5)} var(--space-1)`,
          borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--bd)',
          background: value > 0 ? 'rgba(var(--acr),0.06)' : 'var(--ib)',
          color: value > 0 ? 'var(--tx)' : hasGhost ? 'var(--tm)' : 'var(--tx)',
          fontStyle: value === 0 && hasGhost ? 'italic' : 'normal',
          fontSize: 'var(--font-size-base)',
          width: '100%',
          minHeight: 'var(--space-6)',
          boxSizing: 'border-box',
          textAlign: 'center',
          fontFamily: "'JetBrains Mono', monospace",
          opacity: disabled ? 0.5 : 1,
          outline: isToday ? '1px solid rgba(var(--acr),0.25)' : undefined,
        }}
        title={hasGhost ? `Forecast suggests ${ghost}h. Press Enter to accept.` : undefined}
      />
      {menuPos && (
        <CellContextMenu
          x={menuPos.x}
          y={menuPos.y}
          canPaste={hasHourClipboard()}
          onClose={() => setMenuPos(null)}
          onCopy={() => copyHourValue(draft.trim() === '' ? 0 : parseFloat(draft) || 0)}
          onCut={() => {
            copyHourValue(draft.trim() === '' ? 0 : parseFloat(draft) || 0);
            setDraft('');
            commit('');
          }}
          onPaste={() => {
            const clip = readHourClipboard();
            if (clip !== null) {
              setDraft(clip > 0 ? String(clip) : '');
              commit(String(clip));
            }
          }}
        />
      )}
    </div>
  );
}
